import React, { useId } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';

/**
 * Karl — der Dino aus dem DiNo-Logo (#1336).
 *
 * Nachgezeichnet auf dem Blockraster des Logos (7 × 6 Einheiten): Kopf mit
 * rundem Auge, treppenförmiger Hals und Schwanz, Arm-Stummel, zwei
 * viertelrunde Beine. In Ruhe atmet und blinzelt er; denkt er nach, trippelt
 * er. Wer im System „Bewegung reduzieren" eingestellt hat, bekommt ihn still.
 */

const BREITE = 7;
const HOEHE = 6;

const atmen = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-0.12px); }
`;
const blinzeln = keyframes`
  0%, 92%, 100% { opacity: 0; }
  94%, 98% { opacity: 1; }
`;
const beinVorn = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-0.35px); }
`;
const beinHinten = keyframes`
  0%, 100% { transform: translateY(-0.35px); }
  50% { transform: translateY(0); }
`;
const nicken = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-0.15px); }
`;

export const KarlDino: React.FC<{
  /** Höhe in Pixeln; die Breite folgt dem Logo. */
  groesse?: number;
  /** `denkt`: trippelt. `ruhe`: atmet und blinzelt. */
  modus?: 'ruhe' | 'denkt';
  farbe?: string;
  titel?: string;
}> = ({ groesse = 24, modus = 'ruhe', farbe = 'currentColor', titel }) => {
  const maske = `karl-auge-${useId().replace(/:/g, '')}`;
  const denkt = modus === 'denkt';

  return (
    <Box
      component='svg'
      role={titel ? 'img' : undefined}
      aria-hidden={titel ? undefined : true}
      viewBox={`-0.1 -0.1 ${BREITE + 0.2} ${HOEHE + 0.2}`}
      sx={{
        display: 'block',
        height: groesse,
        width: (groesse * (BREITE + 0.2)) / (HOEHE + 0.2),
        fill: farbe,
        overflow: 'visible',
        '& .karl-koerper': {
          animation: denkt
            ? `${nicken} 0.36s ease-in-out infinite`
            : `${atmen} 2.8s ease-in-out infinite`,
        },
        '& .karl-lid': { animation: `${blinzeln} 4.5s infinite` },
        '& .karl-bein-hinten': {
          animation: denkt
            ? `${beinHinten} 0.36s ease-in-out infinite`
            : 'none',
        },
        '& .karl-bein-vorn': {
          animation: denkt ? `${beinVorn} 0.36s ease-in-out infinite` : 'none',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { animation: 'none !important' },
        },
      }}
    >
      {titel && <title>{titel}</title>}
      <defs>
        <mask id={maske}>
          <rect x='-1' y='-1' width='10' height='10' fill='white' />
          <circle cx='4.5' cy='0.55' r='0.15' fill='black' />
        </mask>
      </defs>
      <g className='karl-koerper'>
        <g mask={`url(#${maske})`}>
          {/* Kopf */}
          <path d='M4.35 0 H6.6 Q7 0 7 0.4 V1.5 Q7 2.05 6.45 2.05 H4 V0.35 Q4 0 4.35 0 Z' />
          {/* Hals und Körper als Treppe */}
          <path d='M3.2 1.95 H5 V3.05 H3 V2.15 Q3 1.95 3.2 1.95 Z' />
          <path d='M2.2 3 H5 V4.05 H2 V3.2 Q2 3 2.2 3 Z' />
          {/* Arm-Stummel */}
          <path d='M4.9 3.4 H5.45 Q5.62 3.4 5.62 3.58 V3.82 H4.9 Z' />
          {/* Bauch, unten zum Hinterbein gerundet */}
          <path d='M1.22 4 H3.45 V4.4 Q3.45 5 2.85 5 H1 V4.22 Q1 4 1.22 4 Z' />
          {/* Schwanzspitze */}
          <path d='M0.22 5 H1 V6 H0.22 Q0 6 0 5.78 V5.22 Q0 5 0.22 5 Z' />
        </g>
        <circle className='karl-lid' cx='4.5' cy='0.55' r='0.16' />
      </g>
      <path
        className='karl-bein-hinten'
        d='M2.5 5 H2.95 Q3.4 5.05 3.4 5.6 V6 H2.5 Z'
      />
      <path
        className='karl-bein-vorn'
        d='M4 5 H4.45 Q4.95 5.05 4.95 5.6 V6 H4 Z'
      />
    </Box>
  );
};
